import { FC, ReactNode } from 'react'

import {
  Center,
  EmptyState,
  ErrorMessage,
  Flex,
  IconRocket,
  Inspector,
  Skeleton
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'

import { getAutomationExecutionsParams } from './helpers'
import { WorkflowLink } from './WorkflowLink'
import { WorkflowInspectorEntityType } from './types'
import { useAutomationExecution } from 'Automations/hooks/useAutomationExecution'
import { usePopulateResolvedNodeDefinitions } from 'Automations/nodeDefinitions'
import { getTasksGroupedByNode } from 'Automations/automationExecutions/overview/getAllTasksGroupedByNode'
import { useExecutionTimeline } from 'Automations/automationExecutions/overview/useExecutionTimeline'
import {
  NodeTimeline,
  PollingSpinner,
  RenderNode,
  WaitingForNextStepItem
} from 'Automations/components'

export const InspectorContent: FC<{
  entityId: string
  entityType: WorkflowInspectorEntityType
}> = ({ entityId, entityType }) => {
  const definitionsReady = usePopulateResolvedNodeDefinitions()

  const automationsExecutionsQuery = useQuery(
    dashboardv99990101Client.getAutomationExecutions,
    getAutomationExecutionsParams(entityType, entityId)
  )

  const latestExecutionId =
    automationsExecutionsQuery.data?.items.at(0)?.id ?? null

  const { isPending, isPolling, automationExecution } = useAutomationExecution({
    executionId: latestExecutionId
  })

  if (automationsExecutionsQuery.isError) return <ErrorState />
  if (automationsExecutionsQuery.isPending || !definitionsReady || isPending)
    return <LoadingState />

  return automationExecution ? (
    <Wrapper
      titleAction={
        <Center boxSize="26px">
          <PollingSpinner
            isPolling={isPolling}
            data-testid="workflows.inspector.content.pollingSpinner"
          />
        </Center>
      }
    >
      <LatestExecution automationExecution={automationExecution} />
    </Wrapper>
  ) : (
    <Wrapper>
      <ListEmptyState />
    </Wrapper>
  )
}

const LatestExecution: FC<{
  automationExecution: Dashboardv99990101Api.Automations.AutomationExecution
}> = ({ automationExecution }) => {
  const nodes = getTasksGroupedByNode(automationExecution)
  const { orderedNodes, showWaitingItem } = useExecutionTimeline({
    nodes,
    isRunning: automationExecution.status === 'RUNNING',
    executionId: automationExecution.id
  })

  return (
    <Flex flexDirection="column" gap="16px" width="100%">
      <WorkflowLink
        automationId={automationExecution.automationId}
        automationVersionId={automationExecution.automationVersionId}
      />
      <NodeTimeline
        data-testid="workflows.inspector.content.nodeTimeline"
        padding="0px"
        width="100%"
        variant="INSPECTOR"
      >
        {orderedNodes.map((node, idx) => (
          <RenderNode
            key={node.nodeId}
            node={node}
            automationExecution={automationExecution}
            isLatestNodeExecution={
              !showWaitingItem && idx === orderedNodes.length - 1
            }
          />
        ))}

        {showWaitingItem && (
          <WaitingForNextStepItem data-testid="workflows.inspector.content.waitingForNextStep" />
        )}
      </NodeTimeline>
    </Flex>
  )
}

const Wrapper: FC<{ children: ReactNode; titleAction?: ReactNode }> = ({
  children,
  titleAction
}) => (
  <Inspector.Content
    title="Workflows"
    titleAction={titleAction}
    data-testid="workflows.inspector.content"
  >
    {children}
  </Inspector.Content>
)

const LoadingState: FC = () => (
  <Wrapper>
    <Flex
      direction="column"
      width="100%"
      height="100%"
      gap="8px"
      data-testid="workflows.inspector.content.loading"
    >
      {Array.from({ length: 15 }).map((_, key) => (
        <Skeleton key={key} />
      ))}
    </Flex>
  </Wrapper>
)

const ErrorState: FC = () => (
  <Wrapper>
    <Center width="100%" height="100%" textAlign="center">
      <ErrorMessage data-testid="workflows.inspector.content.errorMessage" />
    </Center>
  </Wrapper>
)

const ListEmptyState: FC = () => (
  <Center height="100%">
    <EmptyState
      icon={IconRocket}
      iconSize="sm"
      title="No recent workflow activity"
      data-testid="workflows.inspector.content.emptyState"
    />
  </Center>
)
